'use client';

import React, { useId, useState } from 'react';
import { useTranslations } from 'next-intl';
import { PLCTA } from './PLCTA';
import {
  IvContentShell,
  IvSectionHeading,
  IvSectionLabel,
  IvSectionRootWhite,
} from './iv-lounge/ivLoungeSectionLayout';
import {
  submitPerformanceLabCallback,
  TRACKING_PARAMS,
} from 'utils/performanceLabCallback';
import { trackMetaPixelLead } from 'lib/analytics/trackMetaPixelLead';
import { markPerformanceLabBookTourTypeformShown } from './performanceLabBookTourTypeform';
import styles from './PerformanceLabLeadFormSection.module.css';

export type PerformanceLabLeadFormSectionProps = {
  /** Display heading beside the form (caller supplies translated copy). */
  heading: string;
  /** Accessible name for the section landmark. */
  ariaLabel: string;
  /** Optional DOM id (e.g. for in-page anchor links). */
  id?: string;
  /** Optional overline above the heading; defaults to leadForm.common.overline. */
  overline?: string;
  /** Optional analytics data-buttontype for the submit button. */
  submitButtonType?: string;
  /** When set, fires Meta Pixel `Lead` on successful submit with this content_name. */
  metaLeadContentName?: string;
};

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  _hp: '',
};

const DEFAULT_SUBMIT_BUTTON_TYPE = 'performance_lab_lead_send';

export const PerformanceLabLeadFormSection: React.FC<
  PerformanceLabLeadFormSectionProps
> = ({
  heading,
  ariaLabel,
  id,
  overline,
  submitButtonType = DEFAULT_SUBMIT_BUTTON_TYPE,
  metaLeadContentName,
}) => {
  const t = useTranslations('performance-lab');
  const formId = useId();
  const statusId = useId();
  const [values, setValues] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  const fieldPrefix = `pl-lead-${formId.replace(/:/g, '')}`;

  const handleChange =
    (field: keyof typeof initialValues) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({ ...prev, [field]: event.target.value }));
      if (status !== 'idle') {
        setStatus('idle');
        setErrorMessage(null);
      }
    };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus('idle');
    setErrorMessage(null);

    try {
      const trackingParams: Partial<
        Record<(typeof TRACKING_PARAMS)[number], string>
      > = {};
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        TRACKING_PARAMS.forEach((param) => {
          const val = urlParams.get(param);
          if (val) {
            trackingParams[param] = val;
          }
        });
      }

      await submitPerformanceLabCallback({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        _hp: values._hp,
        ...trackingParams,
      });
      setStatus('success');
      setValues(initialValues);
      setConsent(false);
      if (metaLeadContentName) {
        trackMetaPixelLead({ content_name: metaLeadContentName });
      }
      markPerformanceLabBookTourTypeformShown();
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t('leadForm.common.submitError'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <IvSectionRootWhite
      id={id}
      aria-label={ariaLabel}
      data-pl-section="lead-form"
    >
      <IvContentShell className={styles.shell}>
        <div className={styles.introColumn}>
          <IvSectionLabel>
            {overline ?? t('leadForm.common.overline')}
          </IvSectionLabel>
          <IvSectionHeading className={styles.introHeading}>{heading}</IvSectionHeading>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate={false}>
          <div className={styles.honeypot} aria-hidden="true">
            <label className={styles.hiddenLabel} htmlFor={`${fieldPrefix}-hp`}>
              {t('leadForm.common.honeypotLabel')}
            </label>
            <input
              id={`${fieldPrefix}-hp`}
              name="_hp"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={values._hp}
              onChange={handleChange('_hp')}
            />
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.hiddenLabel} htmlFor={`${fieldPrefix}-first-name`}>
                {t('leadForm.common.firstName')}
              </label>
              <input
                className={styles.input}
                id={`${fieldPrefix}-first-name`}
                name="firstName"
                type="text"
                autoComplete="given-name"
                required
                disabled={submitting}
                placeholder={t('leadForm.common.firstNamePlaceholder')}
                value={values.firstName}
                onChange={handleChange('firstName')}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.hiddenLabel} htmlFor={`${fieldPrefix}-last-name`}>
                {t('leadForm.common.lastName')}
              </label>
              <input
                className={styles.input}
                id={`${fieldPrefix}-last-name`}
                name="lastName"
                type="text"
                autoComplete="family-name"
                required
                disabled={submitting}
                placeholder={t('leadForm.common.lastNamePlaceholder')}
                value={values.lastName}
                onChange={handleChange('lastName')}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.hiddenLabel} htmlFor={`${fieldPrefix}-email`}>
              {t('leadForm.common.emailLabel')}
            </label>
            <input
              className={styles.input}
              id={`${fieldPrefix}-email`}
              name="email"
              type="email"
              autoComplete="email"
              required
              disabled={submitting}
              placeholder={t('leadForm.common.emailPlaceholder')}
              value={values.email}
              onChange={handleChange('email')}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.hiddenLabel} htmlFor={`${fieldPrefix}-phone`}>
              {t('leadForm.common.phoneLabel')}
            </label>
            <input
              className={styles.input}
              id={`${fieldPrefix}-phone`}
              name="phone"
              type="tel"
              autoComplete="tel"
              required
              disabled={submitting}
              placeholder={t('leadForm.common.phonePlaceholder')}
              value={values.phone}
              onChange={handleChange('phone')}
            />
          </div>

          <div
            id={statusId}
            role={status === 'error' ? 'alert' : 'status'}
            aria-live="polite"
          >
            {status === 'success' && (
              <p className={styles.formMessage} data-variant="success">
                {t('leadForm.common.submitSuccess')}
              </p>
            )}
            {status === 'error' && errorMessage && (
              <p className={styles.formMessage} data-variant="error">{errorMessage}</p>
            )}
          </div>

          <div className={styles.actions}>
            <PLCTA
              className={styles.fullWidthCTA}
              type="submit"
              $plVariant="primary"
              disabled={submitting || !consent}
              aria-describedby={status !== 'idle' ? statusId : undefined}
              data-buttontype={submitButtonType}
            >
              {submitting
                ? t('leadForm.common.submitting')
                : t('leadForm.common.submit')}
            </PLCTA>
          </div>

          <label className={styles.consent}>
            <input
              className={styles.consentBox}
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              required
            />
            {t('leadForm.common.consent')}
          </label>
        </form>
      </IvContentShell>
    </IvSectionRootWhite>
  );
};
